import * as d3 from "d3";
import { IconNames } from "@ddm-design-system/icon";
import { IX10, X10Level } from "../../../store/installation/types";
import { X10_KEG_TIME_LIMIT_DAYS } from "../../../constants";
import { X10TextVars } from "./useX10Text";

export type X10Translate = (key: string, vars?: X10TextVars) => string;

export interface IX10Issue {
  key: string;
  icon: IconNames;
  level: "danger" | "warning";
  title: string;
  description: string;
}

export const formatX10Date = (ms?: number) => (ms ? d3.timeFormat("%x, %H:%M")(new Date(ms)) : "—");

export const formatTemperature = (value?: number) =>
  value === undefined ? "—" : `${value.toFixed(1)} °C`;

export const formatLitres = (value: number) => value.toFixed(1);

/** Beverage name, or why there is none (no keg, or a beverage the app doesn't know). */
export const x10BeverageLabel = (x10: IX10, t: X10Translate) => {
  if (!x10.hasKeg) {
    return t("no_keg");
  }
  const name = x10.beverageKnown ? (x10.beverage.name || "").trim() : "";
  return name || t("beverage_unknown");
};

// Tailwind classes per status, the same palette the pressure chamber cards use
export const X10_CARD_CLASSES: { [level in X10Level]: string } = {
  ok: "hover:!border-black !border-grey-greenish",
  warning: "!border-warning-warning100 !bg-warning-warning25",
  danger: "!border-alert-alert100 !bg-alert-alert25",
  offline: "!border-grey-grey50 !bg-grey-grey25"
};

const HARDWARE_PARTS: Array<[keyof NonNullable<IX10["health"]>, string]> = [
  ["airPumpError", "hardware_air_pump"],
  ["fanError", "hardware_fan"],
  ["condenserError", "hardware_refrigeration"],
  ["pressureSensorError", "hardware_pressure_sensor"],
  ["voltageError", "hardware_power_supply"]
];

export const x10HardwareParts = (x10: IX10, t: X10Translate) =>
  HARDWARE_PARTS.map(([flag, key]) => ({ key, label: t(key), fault: !!x10.health?.[flag] }));

/** Content key for the machine's headline, e.g. "headline_keg_time_warning". */
export const x10HeadlineKey = (x10: IX10) => {
  const { headline, kegTime, expiration } = x10.evaluation;
  if (headline === "keg_time") {
    return `headline_keg_time_${kegTime === "danger" ? "danger" : "warning"}`;
  }
  if (headline === "expiration") {
    return `headline_expiration_${expiration === "danger" ? "danger" : "warning"}`;
  }
  return `headline_${headline}`;
};

const descriptionVars = (x10: IX10, key: string, t: X10Translate): X10TextVars => ({
  DATE: formatX10Date(x10.lastHeartbeat),
  DAYS: key.startsWith("headline_expiration")
    ? Math.abs(x10.evaluation.daysToExpiry ?? 0)
    : x10.evaluation.daysOnKeg,
  LIMIT: X10_KEG_TIME_LIMIT_DAYS,
  TEMP: formatTemperature(x10.vesselTemperature),
  PARTS: x10HardwareParts(x10, t)
    .filter(part => part.fault)
    .map(part => part.label)
    .join(", ")
});

export const x10HeadlineText = (x10: IX10, t: X10Translate) => {
  const key = x10HeadlineKey(x10);
  const vars = descriptionVars(x10, key, t);
  const description =
    key === "headline_hardware" && !vars.PARTS
      ? t("headline_hardware_generic_description")
      : t(`${key}_description`, vars);
  return { title: t(key), description };
};

/** Active problems on an online, closed machine, most serious first. */
export const x10Issues = (x10: IX10, t: X10Translate): IX10Issue[] => {
  const { evaluation } = x10;
  const issue = (key: string, icon: IconNames, level: "danger" | "warning"): IX10Issue => {
    const vars = descriptionVars(x10, key, t);
    return {
      key,
      icon,
      level,
      title: t(key),
      description:
        key === "headline_hardware" && !vars.PARTS
          ? t("headline_hardware_generic_description")
          : t(`${key}_description`, vars)
    };
  };

  const issues: IX10Issue[] = [];
  if (evaluation.leak) issues.push(issue("headline_leak", "Leak", "danger"));
  if (evaluation.highTemperature) {
    issues.push(issue("headline_temperature", "HighTempServing", "danger"));
  }
  if (evaluation.hardwareError) issues.push(issue("headline_hardware", "FaultySensor", "danger"));
  if (evaluation.kegTime === "danger" || evaluation.kegTime === "warning") {
    issues.push(issue(`headline_keg_time_${evaluation.kegTime}`, "Sleep", evaluation.kegTime));
  }
  if (evaluation.expiration === "danger" || evaluation.expiration === "warning") {
    issues.push(
      issue(`headline_expiration_${evaluation.expiration}`, "CalendarCircle", evaluation.expiration)
    );
  }
  if (evaluation.noKeg) issues.push(issue("headline_no_keg", "Keg", "warning"));
  if (evaluation.empty) issues.push(issue("headline_empty", "Keg", "warning"));
  if (evaluation.coolingDown) issues.push(issue("headline_cooling_down", "Temperature", "warning"));
  return issues;
};
